import './styles.css';
import { FONT_STACK, graphite, mint } from '@madar/shared';
import { plan, snapshot, type SnapshotUnit } from './data';
import { buildBuilding } from './scene/building';
import { Stage, type PresetName } from './scene/stage';
import { Ui } from './ui';

const rootStyle = document.documentElement.style;
for (const [step, hex] of Object.entries(graphite)) rootStyle.setProperty(`--g-${step}`, hex);
for (const [step, hex] of Object.entries(mint)) rootStyle.setProperty(`--m-${step}`, hex);
rootStyle.setProperty('--font', FONT_STACK);

const viewport = document.querySelector<HTMLElement>('#viewport')!;
const overlay = document.querySelector<HTMLElement>('#overlay')!;

const params = new URLSearchParams(location.search);
const building = buildBuilding(plan, snapshot.units);
// ?renderer=webgl forces the fallback, so it can be checked on a machine that has WebGPU.
const stage = new Stage(viewport, building, params.get('renderer') === 'webgl');
const byCode = new Map(snapshot.units.map((u) => [u.code, u]));
// For inspection in development only: the dev build exposes the stage.
if (import.meta.env.DEV) Object.assign(window, { __stage: stage });

let isolated: string | null = null;

const ui = new Ui(overlay, plan.floors, snapshot, {
  preset(name: PresetName) {
    stage.preset(name);
    isolated = null;
    ui.setPreset(name);
    ui.setIsolated(null);
    ui.setExplode(stage.explodeValue);
  },
  explode(value) {
    stage.setExplode(value);
    ui.setPreset(null);
  },
  isolate(key) {
    isolated = key;
    stage.isolate(key);
    ui.setIsolated(key);
    ui.setPreset(null);
    if (key === null) select(null);
  },
  xray(on) {
    stage.setXray(on);
    ui.setPreset(null);
    ui.setXray(on);
  },
  build(value) {
    stage.setBuild(value);
  },
  play() {
    stage.playBuild();
  },
  pause() {
    stage.pauseBuild();
    ui.setBuild(stage.buildValue, false);
  },
  closeCard() {
    select(null);
  },
  focus(unit: SnapshotUnit) {
    stage.onPick({ floor: unit.floor, code: unit.code });
  },
  enquire(unit: SnapshotUnit) {
    // Not sent anywhere yet. Enquiries will reach Madar as prospects through
    // the site Worker; until that route exists the payload is only shown.
    const payload = {
      property: 'mutal',
      code: unit.code,
      floor: unit.floor,
      at: new Date().toISOString(),
    };
    console.info('استفسار', payload);
  },
});

function select(code: string | null) {
  stage.select(code);
  ui.showUnit(code ? (byCode.get(code) ?? null) : null);
}

stage.onBuild = (value, playing) => ui.setBuild(value, playing);

stage.onPick = (pick) => {
  if (!pick) return select(null);
  if (pick.floor !== isolated) {
    isolated = pick.floor;
    stage.isolate(pick.floor);
    ui.setIsolated(pick.floor);
    ui.setPreset(null);
  }
  select(pick.code ?? null);
};

/*
 * A link can open a view: ?view=explode, ?floor=first, ?unit=SG2, ?xray,
 * ?build=0…4. For sharing one space with a prospect, and for taking the
 * same picture twice; ?still holds everything put.
 */
const view = params.get('view');
const start: PresetName = view === 'explode' || view === 'dusk' ? view : 'hero';
stage.preset(start, true);
ui.setPreset(start);
ui.setExplode(stage.explodeValue);
ui.setIsolated(null);
const linkedUnit = byCode.get(params.get('unit') ?? '');
const linkedFloor = linkedUnit?.floor ?? params.get('floor');
if (linkedFloor && plan.floors.some((f) => f.key === linkedFloor)) {
  stage.onPick({ floor: linkedFloor, ...(linkedUnit ? { code: linkedUnit.code } : {}) });
} else if (start === 'hero' && !params.has('still')) {
  // The arrival: the building goes up floor by floor while the camera turns.
  // Not for a link to a unit, nor for a visitor who asked for less motion.
  stage.startOrbit();
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    stage.setBuild(0, true);
    stage.playBuild();
  }
}
if (params.has('xray')) {
  stage.setXray(true);
  ui.setPreset(null);
  ui.setXray(true);
}
const buildParam = Number(params.get('build'));
if (params.has('build') && Number.isFinite(buildParam)) stage.setBuild(buildParam, true);
ui.setBuild(stage.buildValue, false);

stage
  .start()
  .then((backend) => {
    document.documentElement.dataset.backend = backend;
    document.documentElement.classList.add('is-ready');
  })
  .catch((error: unknown) => {
    console.error(error);
    document.documentElement.classList.add('is-ready');
    overlay.append(
      Object.assign(document.createElement('p'), {
        className: 'glass failure',
        textContent: 'تعذّر تشغيل العرض ثلاثي الأبعاد على هذا المتصفح.',
      }),
    );
  });
